import {
    useCallback,
    useRef,
    FC,
    ReactElement,
    ChangeEvent,
    FocusEvent,
    KeyboardEvent,
    FormEvent,
} from 'react';
import classnames from 'classnames';

import ClearIcon from 'dibs-icons/exports/Close';
import SearchIcon from 'dibs-icons/exports/MagnifyingGlass';

// components
import { SIZES, COLORS } from '../Common/constants';
import { StyledButton } from '../Common/Button/StyledButton';
import { Input } from '../Input';
import { RotatingArrow } from '../RotatingArrow';
import { DropdownContent } from './DropdownContent';

// styles
import styles from './main.scss';

const { small, medium, large } = SIZES;

type PropsType = {
    dataTn: string;
    value?: string;
    defaultValue?: string;
    onChange?: (value: string) => void;
    size?: typeof small | typeof medium | typeof large;
    autoComplete?: boolean | string;
    disabled?: boolean;
    focusOnClear?: boolean;
    hasRightSearchButton?: boolean;
    isClearButtonHidden?: boolean;
    noBorder?: boolean;
    onBlur?: (e: FocusEvent<HTMLInputElement>) => void;
    onClear?: () => void;
    onFocus?: (e: FocusEvent<HTMLInputElement>) => void;
    onKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void;
    onSubmit?: (e: FormEvent<HTMLFormElement>) => void;
    placeholder?: string;
    toggleDropdown?: () => void;
    dropdownIsOpen?: boolean;
    dropdownContent?: ReactElement;
    isNavigationRefresh?: boolean;
    isSearchIconHidden?: boolean;
    isTextInputUppercased?: boolean;
    leftMargin?: typeof small;
    isMobile?: boolean;
};

const noop = (): void => {};

export const SearchBar: FC<PropsType> = props => {
    const {
        autoComplete = false,
        disabled = false,
        focusOnClear = false,
        hasRightSearchButton = true,
        noBorder = false,
        dropdownIsOpen = false,
        onBlur = noop,
        onClear = noop,
        onFocus = noop,
        onKeyDown = noop,
        dataTn,
        isClearButtonHidden,
        onChange,
        onSubmit,
        toggleDropdown,
        placeholder,
        size,
        value,
        defaultValue,
        dropdownContent,
        isNavigationRefresh,
        isSearchIconHidden,
        isTextInputUppercased,
        leftMargin,
        isMobile,
    } = props;

    const inputRef = useRef<HTMLInputElement | null>(null);

    const doChange = useCallback(
        (val: string) => {
            if (onChange) {
                onChange(val);
            }
        },
        [onChange]
    );

    const onInputChangeHandler = (e: ChangeEvent<HTMLInputElement>): void => {
        doChange(e.target.value);
    };

    const handleOnBlur = useCallback(
        e => {
            inputRef.current = e.target;
            if (onBlur) {
                onBlur(e);
            }
        },
        [onBlur, inputRef]
    );

    const handleOnClear = useCallback(() => {
        if (
            inputRef &&
            inputRef.current &&
            typeof inputRef.current.focus === 'function' &&
            focusOnClear
        ) {
            inputRef.current.focus();
        }
        doChange('');
        onClear();
    }, [doChange, onClear, inputRef, focusOnClear]);

    const hideClearButton =
        typeof isClearButtonHidden !== 'undefined' ? isClearButtonHidden : !value;

    const showRightSearchButton = hasRightSearchButton && !toggleDropdown;
    const showPrimarySearchButton = showRightSearchButton && !hideClearButton;

    // default to medium size
    let buttonSize = styles.mediumButton;
    let searchButtonSize = styles.mediumSearch;
    if (size === small) {
        buttonSize = styles.smallButton;
        searchButtonSize = styles.smallSearch;
    } else if (size === large) {
        buttonSize = styles.largeButton;
        searchButtonSize = styles.largeSearch;
    }

    const searchButton = (
        <StyledButton
            ariaLabel="Search"
            type="submit"
            dataTn={`${dataTn}-search-button`}
            className={classnames({
                [styles.defaultSearch]: !size,
                [searchButtonSize]: !!size,
                [styles.noClick]: hideClearButton,
                [styles.fullyTransparent]: hideClearButton || !showRightSearchButton,
                [styles.whiteFill]: showPrimarySearchButton,
                [styles.satanColor]: isNavigationRefresh && isTextInputUppercased,
            })}
            disabled={disabled}
            isPrimary={showPrimarySearchButton}
        >
            <SearchIcon />
        </StyledButton>
    );

    const clearButton = (
        <StyledButton
            ariaLabel="Clear search terms"
            type="button"
            dataTn={`${dataTn}-clear`}
            className={classnames(styles.fullyTransparent, {
                [styles.defaultButton]: !size,
                [buttonSize]: !!size,
                [styles.hideButton]: hideClearButton,
                [styles.showButton]: !hideClearButton,
            })}
            disabled={disabled}
            isPrimary={false}
            onClick={handleOnClear}
            tabIndex={hideClearButton ? -1 : undefined}
        >
            <ClearIcon />
        </StyledButton>
    );

    const rightSearchVariation = (
        <Input
            // base input fields
            autoComplete={autoComplete}
            autoCorrect={false}
            dataTn={dataTn}
            disabled={disabled}
            noBorder={noBorder}
            onBlur={handleOnBlur}
            onChange={onInputChangeHandler}
            onFocus={onFocus}
            onKeyDown={onKeyDown}
            placeholder={placeholder}
            size={size}
            spellCheck={false}
            value={value}
            defaultValue={defaultValue}
            title="Search"
            ariaLabel="Search"
            leftMargin={leftMargin}
            isTextInputUppercased={isTextInputUppercased}
            // right variation fields
            rightDecorator={
                <>
                    {clearButton}
                    {searchButton}
                </>
            }
            rightDecoratorClass={classnames(styles.decoratorWrapper, {
                [styles.shiftRight]: !noBorder,
            })}
        />
    );

    const leftSearchVariation = (
        <Input
            // base input fields
            autoComplete={autoComplete}
            autoCorrect={false}
            dataTn={dataTn}
            disabled={disabled}
            noBorder={noBorder}
            borderColor={isNavigationRefresh && isMobile ? COLORS.sassyColorIron : undefined}
            onBlur={handleOnBlur}
            onChange={onInputChangeHandler}
            onFocus={onFocus}
            onKeyDown={onKeyDown}
            placeholder={placeholder}
            size={size}
            spellCheck={false}
            value={value}
            defaultValue={defaultValue}
            title="Search"
            ariaLabel="Search"
            isTextInputUppercased={isTextInputUppercased}
            // left variation fields
            leftDecorator={!isSearchIconHidden && searchButton}
            leftDecoratorClass={styles.decoratorWrapper}
            leftMargin={leftMargin}
            rightDecorator={
                toggleDropdown ? (
                    <>
                        {clearButton}
                        <StyledButton
                            ariaLabel="Expand or collapse dropdown"
                            type="button"
                            className={classnames(styles.fullyTransparent, buttonSize)}
                            dataTn={`${dataTn}-dropdown-button`}
                            disabled={disabled}
                            isPrimary={false}
                            onClick={event => {
                                // stop propagation so that useOutsideClick does not also fire
                                event.nativeEvent.stopImmediatePropagation();
                                toggleDropdown();
                            }}
                        >
                            <RotatingArrow direction={dropdownIsOpen ? 'up' : 'down'} />
                        </StyledButton>
                    </>
                ) : (
                    clearButton
                )
            }
            rightDecoratorClass={styles.decoratorWrapper}
        />
    );

    if (onSubmit) {
        return (
            <>
                <form data-tn={`${dataTn}-form`} onSubmit={onSubmit}>
                    <div className={styles.wrapper}>
                        {showRightSearchButton ? rightSearchVariation : leftSearchVariation}
                    </div>
                </form>
                {toggleDropdown && dropdownContent && (
                    <DropdownContent
                        dropdownContent={dropdownContent}
                        dropdownIsOpen={dropdownIsOpen}
                        toggleDropdown={toggleDropdown}
                    />
                )}
            </>
        );
    }

    const wrapperClassName = classnames(styles.wrapper, {
        [styles.searchIconWithBiggerPadding]: isNavigationRefresh && isTextInputUppercased,
        [styles.openDropdown]: dropdownIsOpen && isNavigationRefresh && isTextInputUppercased,
    });

    return (
        <>
            <div className={wrapperClassName}>
                {showRightSearchButton ? rightSearchVariation : leftSearchVariation}
            </div>
            {toggleDropdown && dropdownContent && (
                <DropdownContent
                    dropdownContent={dropdownContent}
                    dropdownIsOpen={dropdownIsOpen}
                    toggleDropdown={toggleDropdown}
                />
            )}
        </>
    );
};
